import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useUnit } from "effector-react";
import { Timeline as ITimeline, POSTransaction } from "../../stores/types.ts";
import clsx from "clsx";
import styles from "./Timeline.module.scss";
import { TimelineItem } from "./TimelineItem.tsx";
import mapNumber from "../../utils/mapNumber.ts";
import {
  endOfDay,
  format,
  minutesToMilliseconds,
  setHours,
  startOfDay,
  startOfTomorrow,
} from "date-fns";
import parseTimelineValue from "../../utils/dateTime/parseTimelineValue.ts";
import { TIME_FORMAT, TIME_FORMAT_NO_SEC } from "../../utils/dateTime/const.ts";
import { $posNameTooltipInfo } from "../../stores/transactions/model.ts";
import { useMediaQuery } from "@mui/material";
import { normalizeSeekPos } from "../../utils/normalizeSeekPos.tsx";
import { formatInTimeZone } from "date-fns-tz";
 
let RELEASE_MANUAL_TIMEOUT ;
const UPDATE_LONG_TIMELINE_OFFSET_MS = 5000;
 
export type TimelineProps = {
  timeline: ITimeline;
  timeZone?: string;
  seekPos: number | null;
  short?: boolean;
  playbackOn?: boolean;
  manualPosition?: boolean;
  onSetManual?: (manual: boolean) => void;
  timelineStart: number;
  timelineEnd: number;
  setTimelineRegion: React.Dispatch<React.SetStateAction<[number, number]>>;
  timeSpan: number;
  globalTimelineEnd: number;
  globalTimelineStart: number;
  containerRef: React.RefObject<HTMLDivElement>;
  clicked: boolean;
  setClicked: React.Dispatch<React.SetStateAction<boolean>>;
  cameraId: string;
  transactionTime?: number;
  cursorDraggingRef: React.MutableRefObject<boolean>;
  setMovingCursorPosition: React.Dispatch<React.SetStateAction<number | null>>;
  posOn: boolean;
  getCursorTime: (e: MouseEvent, container: HTMLDivElement) => number;
  fetchTimelineTransactions: (args: {
    siteId: string;
    startTime: number;
    endTime: number;
  }) => void;
  timelineTransactions: POSTransaction[];
  resetTimelineTransactions: () => void;
  setActiveTransactionByTimestamp: (timestamp: number) => void;
  showTimeline: boolean;
};
 
export const SHORT_TIMELINE_SHIFT_MS = minutesToMilliseconds(2);
 
const Component = ({
  timeline,
  showTimeline,
  timeZone,
  seekPos,
  short,
  manualPosition,
  onSetManual,
  timelineStart,
  timelineEnd,
  setTimelineRegion,
  timeSpan,
  clicked,
  globalTimelineEnd,
  globalTimelineStart,
  containerRef,
  setClicked,
  cameraId,
  transactionTime,
  cursorDraggingRef,
  playbackOn,
  setMovingCursorPosition,
  timelineTransactions,
  posCamera,
  fetchTimelineTransactions,
  resetTimelineTransactions,
  posOn,
  getCursorTime,
}: TimelineProps) => {
  const posNameTooltipInfo = useUnit($posNameTooltipInfo);
  const isMobile = useMediaQuery("(max-width: 1000px)");
  RELEASE_MANUAL_TIMEOUT = isMobile ? 2000 : 10000;
 
  const lastRangeRef = useRef<{ start: number; end: number } | null>(null);
 
  const formatTime = useCallback(
    (time: number, formatStr: string) =>
      formatInTimeZone(time, timeZone ? timeZone : 'UTC', formatStr),
    [timeZone]
  );
 
  // --- region management ---
  useEffect(() => {
    if (!seekPos || manualPosition || clicked) return;
 
    const dayStart = startOfDay(new Date(seekPos)).getTime();
    const dayEnd = endOfDay(new Date(seekPos)).getTime();
 
    // 🔹 Avoid oscillation when exactly at midnight
    if (seekPos === dayStart) {
      setTimelineRegion([dayStart + 1, dayEnd]);
      return;
    }
 
    let start: number, end: number;
 
    if (short) {
      end = Math.min(seekPos + timeSpan / 2, globalTimelineEnd);
      start = Math.max(end - timeSpan, globalTimelineStart);
      setTimelineRegion([start, end]);
    } else if (isMobile) {
      // Mobile → 4h buckets
      const date = new Date(seekPos);
      const bucketIndex = Math.floor(date.getHours() / 4);
      const startHour = bucketIndex * 4;
      start = setHours(startOfDay(date), startHour).getTime();
      const endHour = startHour + 4;
      end =
        endHour === 24
          ? startOfTomorrow().getTime()
          : setHours(startOfDay(date), endHour).getTime();
      setTimelineRegion([start, end]);
    } else {
      // Desktop → full day
      // ✅ Only shift region if seekPos is outside the current region
      if (seekPos < timelineStart || seekPos > timelineEnd) {
        setTimelineRegion([dayStart + 1, dayEnd]);
      }
    }
  }, [
    seekPos,
    short,
    timeSpan,
    globalTimelineStart,
    globalTimelineEnd,
    clicked,
    manualPosition,
    isMobile,
    setTimelineRegion,
    timelineStart,
    timelineEnd,
  ]);
 
  useEffect(() => {
    if (manualPosition) {
      const timeout = setTimeout(() => {
        onSetManual?.(false);
      }, RELEASE_MANUAL_TIMEOUT);
      return () => {
        clearTimeout(timeout);
      };
    }
    return undefined;
  }, [manualPosition, timelineStart, timelineEnd, onSetManual]);
 
  // --- fetch transactions ---
 
  const fetchingRef = useRef(false);   
 
  // useEffect(() => {
  //   if (!showTimeline) return;
  //   const load = async () => {
  //     if (
  //       !cameraId ||
  //       !Number.isFinite(timelineStart) ||
  //       !Number.isFinite(timelineEnd) ||
  //       short
  //     )
  //       return;
  //     if (timelineEnd <= timelineStart) return;
 
  //     // ✔ SAFE POS CAMERA CHECK
  //     const isPosMatch = posCamera && posCamera === cameraId;
  //     if (!isPosMatch) {
  //       resetTimelineTransactions();
  //       return;
  //     }
 
  //     // 🔹 Normalize to full-day boundaries to prevent duplicate calls
  //     // const roundedStart = startOfDay(new Date(timelineStart)).getTime();
  //     // const roundedEnd = endOfDay(new Date(timelineEnd)).getTime();
 
  //     const roundedStart = startOfDay(timelineStart).getTime();
  //     const roundedEnd = endOfDay(timelineEnd).getTime();
 
 
  //     // 🔹 Skip if already fetched same range
  //     if (
  //       lastRangeRef.current?.start === roundedStart &&
  //       lastRangeRef.current?.end === roundedEnd
  //     ) {
  //       return;
  //     }
 
  //     // 🔹 Skip if a fetch is already running
  //     if (fetchingRef.current) return;
 
  //     fetchingRef.current = true;
  //     lastRangeRef.current = { start: roundedStart, end: roundedEnd };
 
  //     try {
  //       await fetchTimelineTransactions({
  //         siteId: cameraId,
  //         startTime: roundedStart,
  //         endTime: roundedEnd,
  //       });
  //     } catch (err) {
  //       console.error("[Timeline] Failed to fetch transactions", err);
  //     } finally {
  //       fetchingRef.current = false;
  //     }
  //   };
 
  //   load();
  // }, [cameraId, showTimeline, timelineStart, timelineEnd, short, posCamera, fetchTimelineTransactions]);
 
  useEffect(() => {
    if (!showTimeline) return;
 
    const load = async () => {
      if (
        !cameraId ||
        !Number.isFinite(timelineStart) ||
        !Number.isFinite(timelineEnd) ||
        short
      ) return;
 
      if (timelineEnd <= timelineStart) return;
 
      // SAFE POS CAMERA CHECK
      const isPosMatch = posCamera && posCamera === cameraId;
      if (!isPosMatch) {
        resetTimelineTransactions();
        return;
      }
 
      // Normalize to full-day boundaries
      const roundedStart = startOfDay(timelineStart).getTime();
      const roundedEnd = endOfDay(timelineEnd).getTime();
 
      // Skip if already fetched same range
      if (
        lastRangeRef.current?.start === roundedStart &&
        lastRangeRef.current?.end === roundedEnd
      ) {
        return;
      }
 
      // Skip if a fetch is already running
      if (fetchingRef.current) return;
 
      fetchingRef.current = true;
      lastRangeRef.current = { start: roundedStart, end: roundedEnd };
 
      try {
        await fetchTimelineTransactions({
          siteId: cameraId,
          startTime: roundedStart,
          endTime: roundedEnd,
        });
      } catch (err) {
        console.error("[Timeline] Failed to fetch transactions", err);
      } finally {
        fetchingRef.current = false;
      }
    };
 
    load();
  }, [
    cameraId,
    showTimeline,
    timelineStart,
    timelineEnd,
    short,
    posCamera,
    fetchTimelineTransactions,
  ]);
 
  // cleanup
  useEffect(() => resetTimelineTransactions, [resetTimelineTransactions]);
 
  // --- mouse handling ---
  const handleMouseOut = useCallback(() => {
    setClicked(false);
    setMovingCursorPosition(null);
    cursorDraggingRef.current = false;
  }, [setClicked, setMovingCursorPosition, cursorDraggingRef]);
 
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (
        clicked &&
        containerRef.current &&
        "movementX" in e &&
        cursorDraggingRef.current
      ) {
        let cursorTime = getCursorTime(e, containerRef.current);
 
        // Clamp cursorTime to timelineStart/timelineEnd for mobile
        if (isMobile) {
          cursorTime = Math.max(timelineStart, Math.min(cursorTime, timelineEnd));
        }
 
        setMovingCursorPosition(cursorTime);
        onSetManual?.(true);
 
        const leftShift =
          cursorTime <= timelineStart + SHORT_TIMELINE_SHIFT_MS &&
          globalTimelineStart < timelineStart;
        const rightShift =
          cursorTime >= timelineEnd - SHORT_TIMELINE_SHIFT_MS &&
          globalTimelineEnd > timelineEnd;
 
        if (leftShift || rightShift) {
          const diff = leftShift ? -1 : 1;
          setTimelineRegion(([s, e]) => [s + diff, e + diff]);
        }
      }
    },
    [
      clicked,
      containerRef,
      cursorDraggingRef,
      getCursorTime,
      setMovingCursorPosition,
      onSetManual,
      timelineStart,
      timelineEnd,
      globalTimelineStart,
      globalTimelineEnd,
      setTimelineRegion,
    ]
  );
 
  useEffect(() => {
    document.body.addEventListener("mousemove", handleMouseMove);
    document.body.addEventListener("mouseleave", handleMouseOut);
    return () => {
      document.body.removeEventListener("mousemove", handleMouseMove);
      document.body.removeEventListener("mouseleave", handleMouseOut);
    };
  }, [handleMouseMove, handleMouseOut]);
 
  // --- timeline marks ---
  const times = useMemo(() => {
    const times: [number, string][] = [];
    const numItems = short ? 15 :  isMobile ? 4 : 24;
    const rounding = (short ? 60 : 3600) * 1000;
 
    for (let i = 0; i < numItems; i++) {
      const n =
        Math.round(
          mapNumber(i, 0, numItems, timelineStart, timelineEnd) / rounding
        ) * rounding;
      const left = mapNumber(n, timelineStart, timelineEnd, 0, 100) + "%";
      if (
        n > timelineStart &&
        n < timelineEnd &&
        times.findIndex(([t]) => n === t) === -1
      ) {
        times.push([n, left]);
      }
    }
    return times;
  }, [timelineStart, timelineEnd, short]);
 
  const filteredTimeline = useMemo(
    () =>
      timeline.filter(
        (i) =>
          parseTimelineValue(i.start) <= timelineEnd &&
          parseTimelineValue(i.end) >= timelineStart
      ),
    [timeline, timelineStart, timelineEnd]
  );
 
  const tooltipLeft = useMemo(() => {
    if (!posNameTooltipInfo || !containerRef.current) return;
    const box = containerRef.current.getBoundingClientRect();
    const rawLeft = posNameTooltipInfo.clientX - box.left;
    const maxLeft = box.width;
    return Math.max(0, Math.min(rawLeft, maxLeft));
  }, [posNameTooltipInfo, containerRef]);
 
  return (
    <div className={styles.timelineWrapper}>
      {posNameTooltipInfo &&
        Boolean(posNameTooltipInfo.short) === Boolean(short) && (
          <div className={styles.tooltip} style={{ left: `${tooltipLeft}px` }}>
            {posNameTooltipInfo.name}
          </div>
        )}
      <div className={styles.timeline}>
        {filteredTimeline.map((i) => (
          <TimelineItem
            item={i}
            key={i.recordingId}
            timelineStart={timelineStart}
            timelineEnd={timelineEnd}
            timelineTransactions={posOn ? timelineTransactions : []}
            short={short}
          />
        ))}
      </div>
      <div className={styles.times}>
        {[timelineStart, timelineEnd].map((time, i) => (
          <div
            className={clsx(styles.time, {
              [styles.start]: i === 0,
              [styles.end]: i === 1,
            })}
            key={`${time}${i}`}
          >
            {formatTime(time, short ? TIME_FORMAT : TIME_FORMAT_NO_SEC)}
          </div>
        ))}
      </div>
      <div className={styles.dynamicTimes}>
        {times.map(([time, left], i) => (
          <div className={styles.time} style={{ left }} key={`${time}${i}`}>
            {formatTime(time, short ? TIME_FORMAT : TIME_FORMAT_NO_SEC)}
          </div>
        ))}
      </div>
    </div>
  );
};
 
export const Timeline = memo(Component);
 
